import React from "react";

const OurJourneySection = () => {
  return (
    <section className="w-full bg-gradient-to-br from-white via-blue-50/30 to-white">
      <div className="relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-blue-100/20 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-200/10 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl"></div>
        
        <div
          className="
            relative z-10
            mx-auto max-w-7xl
            px-4 sm:px-6 lg:px-8 py-12
          "
        >
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Our Journey
            </span>
            <h2
              className="
                text-[#0b4864]
                font-bold
                text-2xl sm:text-4xl lg:text-5xl
                tracking-tight
                max-w-3xl mx-auto
              "
            >
              Our Journey Through Innovation
            </h2>
            <div className="mt-4 w-20 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto rounded-full"></div>
          </div>

          <div
            className="
              grid grid-cols-1
              lg:grid-cols-2
              gap-10 lg:gap-16
              items-center
            "
          >
            {/* Image with modern frame */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-cyan-400/20 rounded-3xl blur-xl"></div>
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 blur-md"></div>
                <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                  <img
                    src="/assets/our-journey.webp"
                    alt="Journey silhouette at sunset"
                    className="
                      w-full
                      h-[500px]
                      object-cover
                      object-center
                      group-hover:scale-105
                      transition-transform duration-700
                    "
                    loading="lazy"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                  
                  {/* Floating stats */}
                  <div className="absolute bottom-6 right-8 bg-white rounded-2xl shadow-xl p-4 transform rotate-3">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-[#0b4864]">10+</div>
                      <div className="text-sm text-gray-600 mt-1">Professionals</div>
                    </div>
                  </div>
                </div>
                
                {/* Decorative corner elements */}
                <div className="absolute -top-4 -left-4 w-8 h-8 border-t-2 border-l-2 border-blue-500 rounded-tl-lg"></div>
                <div className="absolute -bottom-4 -right-4 w-8 h-8 border-b-2 border-r-2 border-cyan-400 rounded-br-lg"></div>
              </div>
            </div>

            {/* Content with timeline elements */}
            <div className="relative">
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 shadow-xl border border-white/50">
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                        <span className="text-white font-semibold text-base">2020</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Foundation</h3>
                      <p className="mt-2 text-gray-600 leading-relaxed">
                        Established in Bhubaneswar, India, building on years of 
                        global freelance experience in web development and SAP MM 
                        implementation.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-400 rounded-xl flex items-center justify-center">
                        <span className="text-white font-semibold text-base">→</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Growth & Expansion</h3>
                      <p className="mt-2 text-gray-600 leading-relaxed">
                        Assembled a team of highly skilled consultants, recognizing 
                        the growing demand for niche technology expertise across 
                        industries.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-400 rounded-xl flex items-center justify-center">
                        <span className="text-white font-semibold text-base">Now</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Present Success</h3>
                      <p className="mt-2 text-gray-600 leading-relaxed">
                        A team of 10+ professionals serving clients with Supply Chain 
                        Business Consulting, Data & Analytics solutions, and end-to-end 
                        IT implementation services.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Separator line */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-gray-700">Quality</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-gray-700">Value</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-gray-700">Talent</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-gray-700">Agility</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating element */}
              <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-cyan-300/20 rounded-full blur-lg"></div>
            </div>
          </div>

          {/* Bottom quote */}
          <div className="mt-16 lg:mt-20 text-center">
            <div className="inline-block max-w-3xl">
              <div className="relative">
                <svg 
                  className="absolute -top-6 -left-6 w-12 h-12 text-blue-200/50" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <blockquote className="text-xl sm:text-2xl text-gray-700 font-medium italic p-8 bg-white/50 rounded-2xl backdrop-blur-sm border border-white/30">
                  "Our success is driven by a strong commitment to quality, value, 
                  talent, agility, and long-term client partnerships."
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurJourneySection;




// import React from "react";

// const OurJourneySection = () => {
//   return (
//     <section className="w-full bg-white">
//       <div
//         className="
//           mx-auto max-w-7xl
//           px-4 sm:px-6 lg:px-8
//           py-12 sm:py-16 lg:py-20
//         "
//       >
//         <div
//           className="
//             grid grid-cols-1
//             md:grid-cols-2
//             gap-8 md:gap-12 lg:gap-16
//             items-center
//           "
//         >
//           {/* Image */}
//           <div className="flex justify-center md:justify-start">
//             <div className="w-full max-w-sm sm:max-w-md lg:max-w-lg">
//               <img
//                 src="/assets/our-journey.webp"
//                 alt="Journey silhouette at sunset"
//                 className="
//                   w-full
//                   object-cover
//                   rounded-2xl
//                   aspect-[4/5]
//                 "
//                 loading="lazy"
//               />
//             </div>
//           </div>

//           {/* Content */}
//           <div>
//             <h2
//               className="
//                 text-[#0b4864]
//                 font-bold
//                 text-2xl sm:text-3xl lg:text-4xl
//               "
//             >
//               Our Journey
//             </h2>

//             <p
//               className="
//                 mt-4
//                 text-gray-700
//                 text-sm sm:text-base
//                 leading-7 sm:leading-8
//                 max-w-2xl
//               "
//             >
//               Digital Transformation Associates was established in 2020 in
//               Bhubaneswar, India. Building on years of freelance experience in
//               web development and SAP MM implementation projects delivered
//               globally. Recognizing the growing demand for niche technology
//               expertise, the company began by assembling a team of highly
//               skilled web development consultants. Today, DTA has grown to a
//               team of 10+ professionals serving clients across Odisha,
//               delivering Supply Chain Business Consulting, Data & Analytics
//               solutions, end-to-end IT implementation, and application support
//               services. Our success is driven by a strong commitment to quality,
//               value, talent, agility, and long-term client partnerships.
//             </p>
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default OurJourneySection;
